import { useState, useEffect, useMemo, useCallback, memo } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import authService from '../services/authService'
import { DollarSign, Star, Plus, Activity, Award, Target, User, FolderOpen, Eye, Edit, TrendingUp, Lock } from 'lucide-react'
import { getCurrencySymbol } from '../constants/index.js'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Skeleton } from '../components/ui/Skeleton'
import { DashboardStatsSkeleton, DashboardProjectsSkeleton } from '../components/ui/EnhancedSkeleton'
import Logo from '../components/ui/Logo'
import LazyImage from '../components/ui/LazyImage'
import { PROJECT_STATUS } from '../constants/index.js'
import ProfileCompletionBanner from '../components/profile/ProfileCompletionBanner'
import ProfileCompletionModal from '../components/profile/ProfileCompletionModal'

const DashboardPage = memo(() => {
  const { user, hasCapability, isProfileComplete } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [recentActivity, setRecentActivity] = useState([])
  const [projects, setProjects] = useState([])
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [showCompletionModal, setShowCompletionModal] = useState(false)

  // Memoized animation variants for better performance
  const fadeInUp = useMemo(() => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  }), [])

  const staggerChildren = useMemo(() => ({
    animate: { transition: { staggerChildren: 0.1 } }
  }), [])

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchDashboardData = useCallback(async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      setProjectsLoading(true)
      
      // Fetch all dashboard data in a single API request for better performance
      const dashboardData = await authService.getDashboardData()

      // Set stats data
      setStats(dashboardData.stats || {
        totalDonated: user.total_donated || 0,
        projectsSupported: 0,
        badgeLevel: user.badge_level || 'BRONZE'
      })

      // Set projects data
      setProjects(dashboardData.projects || [])

      // Set recent activity data
      setRecentActivity(dashboardData.recentActivity || [])

    } catch (err) {
      console.error('Failed to fetch dashboard data:', err)
      
      // Fallback data
      setStats({
        totalDonated: user.total_donated || 0,
        projectsSupported: 0,
        badgeLevel: user.badge_level || 'BRONZE'
      })
      setRecentActivity([])
      setProjects([])
    } finally {
      setLoading(false)
      setProjectsLoading(false)
    }
  }, [user?.id, user?.total_donated, user?.badge_level])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  // Profile completion handlers
  const handleCompleteProfile = useCallback(() => {
    setShowCompletionModal(false)
    navigate('/profile')
  }, [navigate])

  const handleCreateProject = useCallback(() => {
    if (hasCapability('create_project')) {
      navigate('/create')
    } else {
      setShowCompletionModal(true)
    }
  }, [hasCapability, navigate])

  const LoadingSkeleton = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <Card className="gradient-hero p-4 sm:p-6 lg:p-8 text-white border-0 relative overflow-hidden">
        <div className="absolute top-4 left-4">
          <Logo variant="mark" size="medium" theme="light" alt="Crowblock" />
        </div>
        <div className="pt-12">
          <Skeleton className="h-6 sm:h-8 bg-white/20 mb-2" />
          <Skeleton className="h-3 sm:h-4 bg-white/20 w-2/3" />
        </div>
      </Card>
      
      <DashboardStatsSkeleton />
      
      <Card className="p-4 sm:p-6">
        <Skeleton className="h-4 sm:h-6 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 sm:h-16" />
          ))}
        </div>
      </Card>
    </motion.div>
  )

  const getStatusConfig = (status) => {
    const configs = {
      [PROJECT_STATUS.DRAFT]: { color: 'bg-gray-100 text-gray-800', text: 'Draft' },
      [PROJECT_STATUS.SUBMITTED]: { color: 'bg-blue-100 text-blue-800', text: 'Under Review' },
      [PROJECT_STATUS.APPROVED]: { color: 'bg-green-100 text-green-800', text: 'Approved' },
      [PROJECT_STATUS.REJECTED]: { color: 'bg-red-100 text-red-800', text: 'Rejected' },
      [PROJECT_STATUS.ACTIVE]: { color: 'bg-primary-teal/10 text-primary-teal', text: 'Active' },
      [PROJECT_STATUS.COMPLETED]: { color: 'bg-purple-100 text-purple-800', text: 'Completed' },
      [PROJECT_STATUS.CANCELLED]: { color: 'bg-orange-100 text-orange-800', text: 'Cancelled' }
    }
    return configs[status] || { color: 'bg-gray-100 text-gray-800', text: status }
  }

  // Memoized stats data - removed Global Rank as requested
  // Move this BEFORE the loading check to maintain hook order
  const statsData = useMemo(() => {
    if (!stats) return []

    const formatAmount = (amount = 0, currency = 'PI') => {
      const safeAmount = Number(amount) || 0
      const formatted = safeAmount.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })

      if (currency === 'PI') {
        return `π${formatted}`
      }

      return `${getCurrencySymbol(currency)}${formatted}`
    }

    const buildCurrencyBreakdown = (entries = {}) => {
      if (!entries || typeof entries !== 'object') return []

      return Object.entries(entries)
        .filter(([, value]) => Number(value) > 0)
        .map(([currency, value]) => ({
          currency,
          formatted: formatAmount(value, currency)
        }))
    }

    const donationBreakdown = buildCurrencyBreakdown(stats.donationsByCurrency)
    const raisedBreakdown = buildCurrencyBreakdown(stats.totalRaisedByCurrency)

    return [
      {
        label: 'Total Donated',
        value: formatAmount(stats.totalDonated || 0, donationBreakdown[0]?.currency || 'PI'),
        icon: DollarSign,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        change: donationBreakdown.length > 0 ? `Across ${donationBreakdown.length} ${donationBreakdown.length === 1 ? 'currency' : 'currencies'}` : 'Start donating to unlock badges',
        breakdown: donationBreakdown
      },
      {
        label: 'Total Raised',
        value: raisedBreakdown.length > 0 ? raisedBreakdown[0].formatted : 'No funds raised yet',
        icon: TrendingUp,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        change: raisedBreakdown.length > 0 ? 'From your projects' : 'Launch a project to begin',
        breakdown: raisedBreakdown
      },
      {
        label: 'Projects Supported',
        value: stats.projectsSupported || 0,
        icon: Target,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        change: (stats.projectsSupported || 0) > 0 ? `${stats.projectsSupported} projects` : 'Start supporting!'
      },
      {
        label: 'Badge Level',
        value: stats.badgeLevel ? stats.badgeLevel.charAt(0) + stats.badgeLevel.slice(1).toLowerCase() : 'Bronze',
        icon: Award,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        change: 'Level up!'
      }
    ]
  }, [stats])

  if (loading) return <LoadingSkeleton />

  // Memoized ProjectCard component for better performance
  const ProjectCard = memo(({ project, index }) => {
    const statusConfig = getStatusConfig(project.status)
    const isDraft = project.status === PROJECT_STATUS.DRAFT
    
    const handleClick = useCallback(() => {
      navigate(isDraft ? `/create?edit=${project.id}` : `/projects/${project.slug}`)
    }, [isDraft, project.id, project.slug, navigate])

    return (
      <motion.div
        variants={fadeInUp}
        transition={{ delay: index * 0.1 }}
        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border border-neutral-200/80 hover:bg-neutral-50/80 hover:border-primary-teal/20 transition-all duration-300 group backdrop-blur-sm"
      >
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
          {project.cover_image ? (
            <LazyImage
              src={project.cover_image}
              alt={project.title}
              className="w-full h-full"
              fallback={
                <div className="w-full h-full flex items-center justify-center bg-primary-teal/10">
                  <FolderOpen className="w-4 h-4 sm:w-6 sm:h-6 text-primary-teal" />
                </div>
              }
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-primary-teal/10">
              <FolderOpen className="w-4 h-4 sm:w-6 sm:h-6 text-primary-teal" />
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="text-sm sm:text-base font-medium text-neutral-900 mb-1 truncate">
            {project.title || 'Untitled Project'}
          </h4>
          <p className="text-xs sm:text-sm text-neutral-600 mb-2">
            {project.funding_goal ? `Goal: ${getCurrencySymbol(project.currency)}${project.funding_goal}` : 'No funding goal set'}
          </p>
          <div className="flex items-center gap-2">
            <Badge className={`text-xs px-2 py-1 ${statusConfig.color}`}>
              {statusConfig.text}
            </Badge>
            {project.view_count && (
              <div className="hidden sm:flex items-center gap-1 text-xs text-neutral-500">
                <Eye className="w-3 h-3" />
                {project.view_count}
              </div>
            )}
          </div>
        </div>
        
        <Button
          onClick={handleClick}
          size="sm"
          variant="outline"
          className="text-primary-teal border-primary-teal hover:bg-primary-teal hover:text-white transition-all duration-200 text-xs sm:text-sm px-2 sm:px-3"
        >
          {isDraft ? <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> : <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />}
          <span className="hidden sm:inline">{isDraft ? 'Edit' : 'View'}</span>
        </Button>
      </motion.div>
    )
  })

  const EmptyState = ({ icon: Icon, title, subtitle, buttonText, onButtonClick }) => (
    <div className="text-center py-8 sm:py-12">
      <Icon className="w-12 h-12 sm:w-16 sm:h-16 text-neutral-400 mx-auto mb-3 sm:mb-4" />
      <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-2">{title}</h3>
      <p className="text-sm sm:text-base text-neutral-600 mb-4 sm:mb-6 max-w-md mx-auto">{subtitle}</p>
      {buttonText && (
        <Button onClick={onButtonClick} className="bg-primary-teal hover:bg-primary-teal/90">
          <Plus className="w-4 h-4 mr-2" />
          {buttonText}
        </Button>
      )}
    </div>
  )

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={staggerChildren}
      className="space-y-6 sm:space-y-8 pb-20 sm:pb-8"
    >
      {/* Profile Completion Banner */}
      <ProfileCompletionBanner onCompleteProfile={handleCompleteProfile} />
      {/* Hero Welcome Card */}
      <motion.div variants={fadeInUp}>
        <Card className="gradient-hero p-4 sm:p-6 lg:p-8 text-white border-0 shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-12 translate-x-12 sm:-translate-y-16 sm:translate-x-16" />
          <div className="absolute bottom-0 left-0 w-16 h-16 sm:w-24 sm:h-24 bg-white/5 rounded-full translate-y-8 -translate-x-8 sm:translate-y-12 sm:-translate-x-12" />
          <div className="relative">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <Badge variant="secondary" className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                  <Star className="w-3 h-3 mr-1" />
                  {stats.badgeLevel ? stats.badgeLevel.charAt(0) + stats.badgeLevel.slice(1).toLowerCase() : 'Bronze'} Member
                </Badge>
              </div>
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">
              Welcome back, {user?.pi_username || user?.google_name || user?.full_name || 'User'}!
            </h1>
            <p className="text-sm sm:text-base opacity-90 max-w-md">
              Your contributions are making a real difference in the world.
            </p>
          </div>
        </Card>
      </motion.div>

      {/* Stats Grid - Now with 4 columns including Total Raised */}
      <motion.div variants={staggerChildren} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {statsData.map((stat, index) => (
          <motion.div key={stat.label} variants={fadeInUp}>
            <Card className="hover:shadow-lg border-0 shadow-sm hover:shadow-md transition-all duration-300 group backdrop-blur-sm bg-white/80">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-neutral-600 mb-1 truncate">{stat.label}</p>
                     {stat.breakdown?.length > 0 ? (
                       <div className="space-y-1.5">
                         {stat.breakdown.map((item) => (
                           <div
                             key={item.currency}
                             className="flex items-center justify-between rounded-lg bg-neutral-100/60 px-2.5 py-1.5"
                           >
                             <div className="flex items-baseline gap-1.5">
                               <span className="text-sm sm:text-base font-bold text-neutral-900">
                                 {item.formatted}
                               </span>
                               <span className="text-[10px] font-bold uppercase tracking-wider text-primary-teal">
                                 {item.currency}
                               </span>
                             </div>
                           </div>
                         ))}
                       </div>
                     ) : (
                       <p className="text-xl sm:text-2xl font-bold text-neutral-900 mb-1">
                         {stat.value}
                       </p>
                     )}
                     {stat.change && (
                       <p className={`text-xs font-medium mt-1 ${stat.color}`}>
                         {stat.change}
                       </p>
                     )}
                  </div>
                  <motion.div 
                    whileHover={{ scale: 1.1 }}
                    className={`w-10 h-10 lg:w-12 lg:h-12 ${stat.bgColor} rounded-xl flex items-center justify-center group-hover:shadow-sm transition-all duration-300 flex-shrink-0 ml-3`}
                  >
                    <stat.icon className={`w-5 h-5 lg:w-6 lg:h-6 ${stat.color}`} />
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* My Projects - Always show but with different states */}
      <motion.div variants={fadeInUp}>
        <Card className="border-0 shadow-sm backdrop-blur-sm bg-white/90">
          <CardHeader className="pb-3 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5 text-primary-teal" />
                <CardTitle className="text-base sm:text-lg lg:text-xl text-neutral-900">My Projects</CardTitle>
              </div>
              <Button
                onClick={handleCreateProject}
                disabled={!hasCapability('create_project')}
                size="sm"
                className={`text-xs sm:text-sm px-2 sm:px-3 ${
                  hasCapability('create_project')
                    ? 'bg-primary-teal hover:bg-primary-teal/90'
                    : 'bg-neutral-300 hover:bg-neutral-400 cursor-pointer'
                }`}
                title={!hasCapability('create_project') ? 'Complete your profile to create projects' : ''}
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">New Project</span>
                <span className="sm:hidden">New</span>
                {!hasCapability('create_project') && <Lock className="w-3 h-3 ml-1" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!hasCapability('create_project') ? (
              <div className="text-center py-8">
                <Lock className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  Complete Profile to Create Projects
                </h3>
                <p className="text-neutral-600 mb-4">
                  Finish setting up your profile to start creating projects and raising funds for your ideas.
                </p>
                <Button
                  onClick={() => setShowCompletionModal(true)}
                  className="bg-primary-teal hover:bg-primary-teal/90"
                >
                  View Requirements
                </Button>
              </div>
            ) : projectsLoading ? (
              <DashboardProjectsSkeleton />
            ) : projects.length > 0 ? (
              <motion.div variants={staggerChildren} className="space-y-3 sm:space-y-4">
                {projects.map((project, index) => (
                  <ProjectCard key={project.id} project={project} index={index} />
                ))}
              </motion.div>
            ) : (
              <EmptyState
                icon={FolderOpen}
                title="No projects yet"
                subtitle="Create your first project to start raising funds for your ideas"
                buttonText="Create Your First Project"
                onButtonClick={handleCreateProject}
              />
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Recent Activity */}
      <motion.div variants={fadeInUp}>
        <Card className="border-0 shadow-sm backdrop-blur-sm bg-white/90">
          <CardHeader className="pb-3 sm:pb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-primary-teal" />
              <CardTitle className="text-base sm:text-lg lg:text-xl text-neutral-900">Recent Activity</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {recentActivity.length > 0 ? (
              <motion.div variants={staggerChildren} className="space-y-3 sm:space-y-4">
                {recentActivity.map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    variants={fadeInUp}
                    className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl hover:bg-neutral-50/80 transition-all duration-300 group"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-teal/10 rounded-full flex items-center justify-center group-hover:bg-primary-teal/20 transition-colors flex-shrink-0">
                      <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-primary-teal" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm sm:text-base font-medium text-neutral-900 mb-1">
                        Donated {getCurrencySymbol(activity.currency || 'PI')}{activity.amount} to
                      </p>
                      <p
                        className="text-xs sm:text-sm text-primary-teal font-medium truncate cursor-pointer hover:underline"
                        onClick={() => activity.projectSlug && navigate(`/projects/${activity.projectSlug}`)}
                      >
                        {activity.project}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs sm:text-sm text-neutral-500">
                        {activity.date}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <EmptyState
                icon={Activity}
                title="No recent activity"
                subtitle="Start supporting projects to see your activity here"
              />
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={staggerChildren} className={`grid gap-4 sm:gap-6 ${hasCapability('create_project') ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
        <motion.div variants={fadeInUp}>
          <Card 
            className="gradient-primary text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group backdrop-blur-sm"
            onClick={() => navigate('/projects')}
          >
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Browse Projects</h3>
                  <p className="text-xs sm:text-sm opacity-90">Discover new projects to support</p>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1, x: 5 }}
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm"
                >
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
                </motion.div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div variants={fadeInUp}>
          <Card
            className={`border-2 transition-all duration-300 cursor-pointer group backdrop-blur-sm bg-white/90 ${
              hasCapability('create_project')
                ? 'border-primary-teal text-primary-teal hover:bg-primary-teal/5'
                : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'
            }`}
            onClick={handleCreateProject}
          >
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2 flex items-center gap-2">
                    Submit Project
                    {!hasCapability('create_project') && <Lock className="w-4 h-4" />}
                  </h3>
                  <p className="text-xs sm:text-sm">
                    {hasCapability('create_project')
                      ? 'Share your project with the community'
                      : 'Complete profile to unlock project creation'
                    }
                  </p>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1, rotate: hasCapability('create_project') ? 90 : 0 }}
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                    hasCapability('create_project')
                      ? 'bg-primary-teal/10 group-hover:bg-primary-teal/20'
                      : 'bg-neutral-200 group-hover:bg-neutral-300'
                  }`}
                >
                  {hasCapability('create_project') ? (
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </motion.div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onCompleteProfile={handleCompleteProfile}
      />

    </motion.div>
  )
})

DashboardPage.displayName = 'DashboardPage'

export default DashboardPage
